// The admin API registry's customer routes: each verb's grant, tenant source and response DTO.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute, type TenantSource } from './define-admin-route';
// Type-only imports (erased at build) — keep the registry client-bundle-safe.
import type { CustomerStatsDto } from '../../schemas/responses/customer-stats';
import type { CustomerFilterOptionsDto } from '../../schemas/responses/customer-filter-options';
import type { CustomerSearchResultDto } from '../../schemas/responses/customer-search';
import type { CustomerActivityDto } from '../../schemas/responses/customer-activity';
import type {
  SellerCommissionReadResult,
  SellerCommissionWriteResult,
} from '../../schemas/responses/commission-write';
import type { CustomerAccountDto } from '../../schemas/responses/customer-account';
import type { AppliedOffsetDto, CustomerOffsetsDto } from '../../schemas/responses/customer-offsets';
import type { SellerPayoutMethodDto } from '../../schemas/responses/seller-payout-method';
import type { BankAccountVerifiedDto } from '../../schemas/responses/bank-account-verification';
import type { CustomerArchiveCheckDto, CustomerStandingResultDto } from '../../schemas/responses/customer-standing';
import type {
  CustomerDocumentArchivedDto,
  CustomerDocumentDownloadDto,
} from '../../schemas/responses/customer-documents';
import type {
  CustomerDetailDto,
  CustomerSaveAckDto,
  CustomerDocument,
  CustomerListResult,
  CustomerCreateResultDto,
  CustomerDuplicateCheckDto,
} from '../../schemas/responses/customer';

/**
 * A document named by `:documentId`, which must belong to the `:id` customer
 * (BL-2208, ADR 062 — Access Grants `within`). Another customer's or another
 * tenant's document, a deleted one and a non-uuid id are all the gate's 404.
 */
const DOCUMENT_OF_CUSTOMER = {
  from: 'record',
  table: 'customer_document',
  param: 'documentId',
  within: { column: 'customerId', param: 'id' },
} as const satisfies TenantSource;

export const customers = {
  // BL-2187: `actions` (POST /api/customers/actions — an eight-action
  // catch-all with its tenant in the body) was DELETED. Its three live moves
  // are the record-scoped `id.standing` / `id.archive` / `id.restore` below.
  check_duplicate: defineAdminRoute({
      path: '/api/customers/check-duplicate',
      params: {},
      // BL-1833: only reached from the Add Customer modal, which needs create.
      // BL-2197: DTO carrier (ADR 027), so the modal reads `data.exists` with no `.raw()`.
      methods: {
        GET: {
          grant: 'customer.create',
          tenant: { from: 'input', field: 'tenantId', in: 'query' },
          response: responseType<CustomerDuplicateCheckDto>(),
        },
      },
    }),
  create: defineAdminRoute({
      path: '/api/customers/create',
      params: {},
      // Response DTO carrier (ADR 027 / BL-899): POST returns the created
      // customer's { id, customerNumber, accountName } under the standard
      // envelope's `data`; the client infers CustomerCreateResultDto with no <T>.
      methods: {
        POST: { grant: 'customer.create', tenant: { from: 'input', field: 'tenantId', in: 'body' }, response: responseType<CustomerCreateResultDto>() },
      },
    }),
  // BL-910: `dropdown_list` (/api/customers/dropdown-list) was DELETED. It
  // preloaded every customer in the tenant into a non-searchable <Select>; a
  // customer picker is a server-backed type-ahead against `search` below.
  filter_options: defineAdminRoute({
      path: '/api/customers/filter-options',
      params: {},
      // Response DTO carrier (ADR 027 / BL-855): GET returns the filter-dropdown
      // options; the client infers CustomerFilterOptionsDto with no <T>.
      methods: {
        GET: { grant: 'customer.read_limited', tenant: { from: 'input', field: 'tenantId', in: 'query' }, response: responseType<CustomerFilterOptionsDto>() },
      },
    }),
  id: {
    commission: defineAdminRoute({
        path: '/api/customers/:id/commission',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-1145): the seller rung of the
        // commission cascade — a seller's standing agreement. BL-1145 removed
        // `commissionSchedule` from the customer PATCH, which was a second,
        // separately-validated write path for this same column; this is now the
        // ONLY writer of `customers.commission_schedule`.
        //
        // BL-1148 added the GET: the customer detail page's commission card
        // self-fetches its cascade here (ADR 029), and `commissionSchedule` left
        // the big `GET /api/customers/:id` envelope in the same PR — one read
        // path for the value, behind one permission gate (ADR 044).
        methods: {
          // BL-1833: the route read terms for accounting-tab OR manage holders;
          // every role holding manage also holds the accounting tab, so the one
          // grant is the same role set.
          GET: {
            grant: 'customer.read_accounting',
            tenant: { from: 'record', table: 'customer', param: 'id' },
            response: responseType<SellerCommissionReadResult>(),
          },
          PUT: { grant: 'customer.update', tenant: { from: 'record', table: 'customer', param: 'id' }, response: responseType<SellerCommissionWriteResult>() },
        },
      }),
    account: defineAdminRoute({
        path: '/api/customers/:id/account',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-909): GET is the Edit Account
        // modal's self-fetch (ADR 029); PUT is the single transactional save
        // that replaced the old contact-write + customer-PATCH fan-out. GET
        // returns the CustomerAccountDto the client infers with no <T>; PUT acks
        // with the id (BL-2410), as PATCH :id does, since the page re-reads.
        methods: {
          GET: { grant: 'customer.read_limited', tenant: { from: 'record', table: 'customer', param: 'id' }, response: responseType<CustomerAccountDto>() },
          PUT: { grant: 'customer.update', tenant: { from: 'record', table: 'customer', param: 'id' }, response: responseType<CustomerSaveAckDto>() },
        },
      }),
    // BL-2518 (ADR 065 — Money Model § 12.9): Apply offsets, one route for the
    // invoice page, the settlement page and the Accounting tab (ADR 044 — Must
    // Consolidate). GET is what the customer owes and is owed: either read
    // grant opens it, and the side the caller can't read comes back null. POST
    // writes both ledger halves; it needs settlement edit here, and the handler
    // also requires `payment.create`, since the invoice half is an invoice
    // payment (ledger_entries.ts: a settlement-only role never moves invoice money).
    offsets: defineAdminRoute({
        path: '/api/customers/:id/offsets',
        params: { id: '' },
        methods: {
          GET: {
            grant: { anyOf: ['invoice.read', 'settlement.read'] },
            tenant: { from: 'record', table: 'customer', param: 'id' },
            response: responseType<CustomerOffsetsDto>(),
          },
          POST: {
            grant: 'settlement.update',
            tenant: { from: 'record', table: 'customer', param: 'id' },
            response: responseType<AppliedOffsetDto>(),
          },
        },
      }),
    // BL-2191 (ADR 059 — Search Spine): one page of the customer's activity,
    // searched, filtered and sorted on the server, typed per row kind. The
    // Activity tab reads it since BL-2192; BL-2207 deleted the old
    // auction_history read it replaced (ADR 044 — Must Consolidate).
    activity: defineAdminRoute({
        path: '/api/customers/:id/activity',
        params: { id: '' },
        methods: {
          GET: {
            grant: 'customer.read_limited',
            tenant: { from: 'record', table: 'customer', param: 'id' },
            response: responseType<CustomerActivityDto>(),
            query: {
              search: '', group: '', page: '', sortField: '', sortDir: '', type: '', role: '',
              auction: '', category: '', date: '', date_range: '',
              // BL-2195: `0` is the tab badge's count-only read; no other size is honored.
              pageSize: '',
            },
          },
        },
      }),
    // BL-2461: the `communication_logs` subtree (the CRM tab's log) was
    // deleted with the tab, and BL-2706 dropped its table.
    // BL-909: the `contacts` subtree (POST /contacts, PATCH+DELETE
    // /contacts/:contactId) was deleted with its routes. Its only caller was
    // the customer-detail page's Edit Account save, which now writes the
    // contact and the customer row in one transactional
    // `PUT /api/customers/:id/account`. Leaving unreachable session-gated
    // routes registered would trip the dead-route check; tagging them
    // `caller: 'external'` to silence it would make the generated API-surface
    // doc advertise a `getApiUser()`-gated route as server-to-server. Deleting
    // is the honest fix. (BUG-1017, filed against the permission gap on the
    // PATCH/DELETE pair, is moot by removal.)
    // BL-2189: a customer's documents, addressed by id under the customer.
    // Download and delete moved here from `/api/files/{download,delete}`;
    // upload stays on `/api/files` (PRJ-84 M5). The tenant is the customer's.
    // BL-2205: the list moved here off `GET /api/customers/:id`.
    documents: {
      index: defineAdminRoute({
          path: '/api/customers/:id/documents',
          params: { id: '' },
          methods: {
            // Every customer read grant, as the download admits; restricted rows
            // are withheld in the service for a caller without the accounting tab (M11).
            GET: {
              grant: { anyOf: ['customer.read', 'customer.read_limited', 'customer.read_accounting'] },
              tenant: { from: 'record', table: 'customer', param: 'id' },
              response: responseType<CustomerDocument[]>(),
            },
          },
        }),
      documentId: {
        index: defineAdminRoute({
            path: '/api/customers/:id/documents/:documentId',
            params: { id: '', documentId: '' },
            methods: {
              // Soft delete (ADR 018 — Soft Delete). The grant is the one the
              // old `/api/files/delete` customer arm used: Admin and Accounting.
              DELETE: {
                grant: 'customer.update_documents',
                tenant: DOCUMENT_OF_CUSTOMER,
                response: responseType<CustomerDocumentArchivedDto>(),
              },
            },
          }),
        download: defineAdminRoute({
            path: '/api/customers/:id/documents/:documentId/download',
            params: { id: '', documentId: '' },
            methods: {
              // Every customer read grant, as the old customer-documents bucket
              // admitted; restricted rows are re-checked in the service (M11).
              GET: {
                grant: { anyOf: ['customer.read', 'customer.read_limited', 'customer.read_accounting'] },
                tenant: DOCUMENT_OF_CUSTOMER,
                response: responseType<CustomerDocumentDownloadDto>(),
              },
            },
          }),
      },
    },
    // BL-2812 — staff enter a customer's bank verification code (the 1¢
    // microdeposit's FNX code) from the Payment methods card. A payment write:
    // `payment.create`, the Receive payment grant (Michael Lawrie, 2026-10-27).
    // The instrument must be this customer's: another's, a removed one or a
    // non-uuid id is the gate's 404.
    payment_instruments: {
      instrumentId: {
        verify: defineAdminRoute({
            path: '/api/customers/:id/payment-instruments/:instrumentId/verify',
            params: { id: '', instrumentId: '' },
            methods: {
              POST: {
                grant: 'payment.create',
                tenant: {
                  from: 'record',
                  table: 'payment_instrument',
                  param: 'instrumentId',
                  within: { column: 'customerId', param: 'id' },
                },
                response: responseType<BankAccountVerifiedDto>(),
              },
            },
          }),
      },
    },
    // BL-2568 — the seller's payout method on the Accounting tab's Payment methods
    // card. The read follows the card's own grant. Recording one is a payment
    // write (Admin and Accounting hold it). Both answer the full method.
    payout_method: defineAdminRoute({
        path: '/api/customers/:id/payout-method',
        params: { id: '' },
        methods: {
          GET: {
            grant: 'payment.read',
            tenant: { from: 'record', table: 'customer', param: 'id' },
            response: responseType<SellerPayoutMethodDto>(),
          },
          PUT: {
            grant: 'payment.create',
            tenant: { from: 'record', table: 'customer', param: 'id' },
            response: responseType<SellerPayoutMethodDto>(),
          },
        },
      }),
    dl_on_file: defineAdminRoute({
        path: '/api/customers/:id/dl-on-file',
        params: { id: '' },
        // BL-2199: DTO carrier (ADR 027). BL-2436: the customer-save ack — the
        // toggle re-reads the customer after a confirmed save.
        methods: {
          PATCH: {
            grant: 'customer.update',
            tenant: { from: 'record', table: 'customer', param: 'id' },
            response: responseType<CustomerSaveAckDto>(),
          },
        },
      }),
    // BL-2187 — a customer's standing moves, one record-scoped route each.
    // `withDeleted`: the tenant still resolves for a DELETED customer, so a
    // standing change on one reaches the service's guard and is refused with a
    // reason (409) instead of a gate 404, and restore can reach its target at
    // all. Access never widens: tenant + membership + the grant still hold.
    //
    // BL-2420 (ADR 062 — Access Grants): a standing move declares
    // `customer.set_standing`; delete, its check and restore declare
    // `customer.archive`. Neither rides on `customer.update` (editing details).
    standing: defineAdminRoute({
        path: '/api/customers/:id/standing',
        params: { id: '' },
        methods: {
          POST: {
            grant: 'customer.set_standing',
            tenant: { from: 'record', table: 'customer', param: 'id', withDeleted: true },
            response: responseType<CustomerStandingResultDto>(),
          },
        },
      }),
    archive: defineAdminRoute({
        path: '/api/customers/:id/archive',
        params: { id: '' },
        methods: {
          POST: {
            grant: 'customer.archive',
            tenant: { from: 'record', table: 'customer', param: 'id', withDeleted: true },
            response: responseType<CustomerStandingResultDto>(),
          },
        },
      }),
    // BUG-1994 — the read-only half of Delete (ADR 026 — Server Orchestration):
    // what blocks deleting this customer right now, read when the Delete dialog
    // opens so it can list the blockers instead of offering a Delete that can
    // only fail. Same grant and tenant source as `archive`, which re-checks.
    archive_check: defineAdminRoute({
        path: '/api/customers/:id/archive-check',
        params: { id: '' },
        methods: {
          GET: {
            grant: 'customer.archive',
            tenant: { from: 'record', table: 'customer', param: 'id', withDeleted: true },
            response: responseType<CustomerArchiveCheckDto>(),
          },
        },
      }),
    restore: defineAdminRoute({
        path: '/api/customers/:id/restore',
        params: { id: '' },
        methods: {
          POST: {
            grant: 'customer.archive',
            tenant: { from: 'record', table: 'customer', param: 'id', withDeleted: true },
            response: responseType<CustomerStandingResultDto>(),
          },
        },
      }),
    index: defineAdminRoute({
        path: '/api/customers/:id',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-899): GET returns the page's
        // CustomerDetailDto (BL-2209); PATCH acknowledges the save with the id
        // only (BL-2195) — every caller re-reads the GET after a confirmed save.
        // BUG-1199 retired the DELETE verb — customers soft-archive instead.
        methods: {
          GET: { grant: 'customer.read_limited', tenant: { from: 'record', table: 'customer', param: 'id' }, response: responseType<CustomerDetailDto>() },
          PATCH: { grant: 'customer.update', tenant: { from: 'record', table: 'customer', param: 'id' }, response: responseType<CustomerSaveAckDto>() },
        },
      }),
  },
  index: defineAdminRoute({
      path: '/api/customers',
      params: {},
      // Response DTO carrier (ADR 027 / BL-899): GET returns the paginated customer
      // list; the client infers CustomerListResult with no <T>, and `respond()`
      // type-checks the route against this same declaration.
      methods: {
        GET: { grant: 'customer.read_limited', tenant: { from: 'input', field: 'tenantId', in: 'query' }, response: responseType<CustomerListResult>() },
      },
    }),
  search: defineAdminRoute({
      path: '/api/customers/search',
      params: {},
      // Response DTO carrier (ADR 027 / BL-855): GET returns tenant-scoped customer
      // search hits; the client infers CustomerSearchResultDto[] with no <T>.
      // BL-1833 AC 13: two roles with no permission in common search here —
      // Cataloger's seller picker on the item form (inventory.update) and
      // Accounting's customer pickers, such as Add buyer and the post-sale
      // pickers (customer.read_limited). The tenant is required; the old
      // only-tenant fallback is gone.
      methods: {
        GET: {
          grant: { anyOf: ['inventory.update', 'customer.read_limited'] },
          tenant: { from: 'input', field: 'tenantId', in: 'query' },
          response: responseType<CustomerSearchResultDto[]>(),
        },
      },
    }),
  stats: defineAdminRoute({
      path: '/api/customers/stats',
      params: {},
      // Response DTO carrier (ADR 027 / BL-855): GET returns tenant customer count
      // stats; the client infers CustomerStatsDto with no <T>.
      methods: {
        GET: { grant: 'customer.read_limited', tenant: { from: 'input', field: 'tenantId', in: 'query' }, response: responseType<CustomerStatsDto>() },
      },
    }),
};
